import React from "react";
import { Link } from "react-router";
import { DailyTable } from "@/features/daily/components/DailyTable";
import type { StageSlaVm } from "@/features/daily/components/funnel/funnel-view-model";

type Props = {
    stages: StageSlaVm[];
};

/**
 * FR-021/FR-022: the percentiles of what COMPLETED, next to what is still open past
 * its target. When the evidence is too thin the cell says how thin — never a zero,
 * never an interpolation. "Vencidas" is the count of breaches, never the size of the
 * sample (SC-021), and it links into the exact queue those breaches live in.
 */
const withMethod = (value: string, percentileLabel: string | null): string =>
    percentileLabel === null ? value : `${value} ${percentileLabel}`;

export const StageTimesTable: React.FC<Props> = ({ stages }) => (
    <section className="daily-panel" aria-labelledby="daily-stage-times-title">
        <div className="daily-panel__head">
            <h2 id="daily-stage-times-title">Tiempos y operaciones abiertas</h2>
            <span className="daily-small daily-muted">Plazos de la política vigente</span>
        </div>
        {stages.some((stage) => stage.percentileLabel !== null) ? (
            // D34/D60: an estimate is labelled as one.
            <p className="daily-small daily-muted">«aprox.» marca un percentil que la fuente estimó en vez de calcularlo exacto.</p>
        ) : null}
        <DailyTable
            caption="Duración P50 y P95 de las completadas por etapa, con su muestra, y lo que sigue abierto fuera de plazo"
            columns={[
                {
                    key: "stage",
                    header: "Etapa",
                    render: (stage: StageSlaVm) => (
                        <span className="daily-table__wrap">
                            <strong>{stage.stageLabel}</strong>
                            <br />
                            <span className="daily-muted">{stage.description} {stage.applicabilityLabel}</span>
                        </span>
                    ),
                },
                { key: "target", header: "Plazo", numeric: true, render: (stage: StageSlaVm) => stage.targetLabel },
                {
                    key: "p50",
                    header: "P50",
                    numeric: true,
                    render: (stage: StageSlaVm) =>
                        stage.durationsMeasured ? withMethod(stage.p50Label, stage.percentileLabel) : stage.durationsMissingLabel,
                },
                {
                    key: "p95",
                    header: "P95",
                    numeric: true,
                    render: (stage: StageSlaVm) =>
                        stage.durationsMeasured ? withMethod(stage.p95Label, stage.percentileLabel) : stage.durationsMissingLabel,
                },
                { key: "sample", header: "Muestra completada", render: (stage: StageSlaVm) => stage.sampleLabel },
                {
                    key: "open",
                    header: "Abiertas vencidas",
                    numeric: true,
                    render: (stage: StageSlaVm) => (
                        <Link className="daily-link" to={stage.queueHref} aria-label={`Abrir las ${stage.breachedOpen} vencidas abiertas de ${stage.stageLabel}`}>
                            {stage.breachedOpen}
                        </Link>
                    ),
                },
                { key: "late", header: "Terminadas tarde", numeric: true, render: (stage: StageSlaVm) => String(stage.breachedLate) },
                { key: "age", header: "Antigüedad P95 abiertas", numeric: true, render: (stage: StageSlaVm) => stage.openAgeP95Label },
            ]}
            rows={stages}
            rowKey={(stage) => stage.stage}
            emptyMessage="Ningún plazo de etapa aplica a este alcance y ventana."
        />
    </section>
);
